import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Typography, Button, Table, useToast } from '@crisellevaldez/speculo-ui';
import { Search, CheckCircle } from 'lucide-react';
import { ExpandableTagsCell } from '../../common/ExpandableTagsCell';
import { SearchableSelect } from '../../common/SearchableSelect';
import { formatPhoneNumber } from '../../../utils/formatters';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { useContactsByTags, PAGE_SIZE, Contact } from '../../../hooks/contacts/useContactsByTags';
import { useFlowContactsPreview } from '../../../hooks/flows/useFlowContactsPreview';
import {
  bulkAddToast,
  canAddToFlow,
  contactListPlaceholder,
} from '../../../hooks/flows/flowEnrollment';
import { ChannelConsentIcons } from '../contacts/consent/ChannelConsentIcons';
import { EnrollmentPreviewSummary } from './EnrollmentPreviewSummary';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { useFlowContacts } from '../../../hooks/flows/useFlowContacts';
import { ScheduleFlowModal } from './ScheduleFlowModal';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';

interface AddContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowName: string;
  flowId: string;
  // Additional flow data
  schedulingWindow?: number;
  scheduleOverUnit?: string;
  allowedDays?: string[];
  allowedHours?: {
    start_hour: number;
    end_hour: number;
  };
  excludeHolidays?: boolean;
  transferNumbers?: Record<string, string>;
  timezone?: string;
  scheduledFor?: string;
  description?: string;
  subType?: string;
}

export const AddContactsModal: React.FC<AddContactsModalProps> = ({
  isOpen,
  onClose,
  flowName,
  flowId,
  schedulingWindow,
  scheduleOverUnit,
  allowedDays,
  allowedHours,
  excludeHolidays,
  transferNumbers,
  scheduledFor,
  description,
  subType,
}) => {
  const { isAdmin } = useUser();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [addedContactsCount, setAddedContactsCount] = useState(0);
  const {
    data: tags,
    isLoading: tagsLoading,
    hasMore: tagsHasMore,
    loadMore: tagsLoadMore,
    setSearch: tagsSetSearch,
  } = useUniqueContactTags(true);
  const {
    contacts,
    totalCount,
    isLoading: contactsLoading,
    isFetching,
  } = useContactsByTags(selectedTags, flowId, shouldFetch, currentPage, PAGE_SIZE);
  const {
    preview,
    isFetching: previewFetching,
    isError: previewError,
    refetch: refetchPreview,
  } = useFlowContactsPreview(flowId, selectedTags, shouldFetch);
  const addGate = canAddToFlow({ searched: shouldFetch, previewFetching, preview });

  const { addContactsToFlow, isAdding } = useFlowContacts();
  const { showToast } = useToast();
  const isLoading = tagsLoading || contactsLoading || isAdding;

  const handleTagsChange = (value: string[]) => {
    setSelectedTags(value);
    setShouldFetch(false);
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    // Search again on the same tags re-plans; the first Search fetches on enable.
    if (shouldFetch) void refetchPreview();
    setShouldFetch(true);
  };

  const handleAddToFlow = async () => {
    if (!selectedTags.length) return;

    try {
      const result = await addContactsToFlow(flowId, selectedTags);

      const created = result.created ?? 0;
      const toast = bulkAddToast(result);

      setAddedContactsCount(created);
      showToast(toast);

      // Only show schedule modal when contacts were actually added
      if (created > 0) {
        setShowScheduleModal(true);
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to add contacts to flow',
        variant: 'error',
      });
    }
  };

  // Keep track of modal state for proper cleanup
  useEffect(() => {
    if (!isOpen) {
      setShowScheduleModal(false);
    }
  }, [isOpen]);

  const handleScheduleModalClose = useCallback(() => {
    setShowScheduleModal(false);
    // Reset states after schedule modal is closed
    setSelectedTags([]);
    setShouldFetch(false);
    setCurrentPage(0);
    onClose();
  }, [onClose]);

  const columns = [
    {
      key: 'name',
      header: 'Full Name',
      cell: (item: Contact) => `${item.firstName || ''} ${item.lastName || ''}`.trim() || '-',
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      cell: (item: Contact) => formatPhoneNumber(item.phoneNumber),
    },
    {
      key: 'tags',
      header: 'Tags',
      cell: (item: Contact) => {
        const tags = item.tags;
        if (!tags || !Array.isArray(tags) || tags.length === 0)
          return <span className="text-muted-foreground">-</span>;
        return <ExpandableTagsCell tags={tags} />;
      },
    },
    {
      key: 'call_tester',
      header: 'Call Tester',
      cell: (item: Contact) => (
        <div className="flex items-center">
          {item.call_tester ? (
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      key: 'consent',
      header: 'Consent',
      cell: (item: Contact) => <ChannelConsentIcons consent={item.consent} />,
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen && !showScheduleModal} onClose={onClose} size="2xl">
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <Typography variant="h3">Add Contacts</Typography>
            <Typography variant="small" className="text-muted-foreground">
              Flow: {flowName}
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div data-tour="add-contacts-modal">
            {/* A fixed height: the table takes what the tag row and summary leave, so the
                window never resizes when the summary appears. */}
            <div className="flex flex-col gap-5 h-[calc(100dvh-140px)] sm:h-[calc(100dvh-160px)] md:h-[calc(100dvh-190px)] 3xl:h-[760px]">
              {!isAdmin && (
                <AdminLockOverlay locked subject="Flow contacts" disableFields={false}>
                  <></>
                </AdminLockOverlay>
              )}
              <div className="shrink-0 flex flex-col sm:flex-row sm:items-start gap-2">
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    placeholder="Select tags"
                    options={tags?.map((tag) => ({ label: tag, value: tag })) || []}
                    value={selectedTags}
                    onChange={handleTagsChange}
                    isLoading={isLoading || isFetching}
                    multiple
                    hasMore={tagsHasMore}
                    onLoadMore={tagsLoadMore}
                    onSearch={tagsSetSearch}
                    searchPlaceholder="Search tags..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSearch}
                    disabled={isLoading || selectedTags.length === 0}
                    className="flex items-center gap-2 whitespace-nowrap h-[38px] flex-1 sm:flex-none justify-center"
                  >
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddToFlow}
                    disabled={isLoading || !selectedTags.length || !isAdmin || !addGate.allowed}
                    className="whitespace-nowrap h-[38px] flex-1 sm:flex-none justify-center"
                    isLoading={isAdding}
                    title={
                      !isAdmin
                        ? 'Only your organization admin can add contacts to flows'
                        : addGate.reason === 'search-first'
                          ? 'Search first to see who will be added'
                          : addGate.reason === 'none-addable'
                            ? 'None of these contacts can be added to this flow'
                            : undefined
                    }
                  >
                    Add to Flow
                  </Button>
                </div>
              </div>

              {shouldFetch && (
                <div className="shrink-0">
                  <EnrollmentPreviewSummary
                    preview={preview}
                    isLoading={previewFetching}
                    isError={previewError}
                  />
                </div>
              )}

              {contacts.length > 0 ? (
                <TableCard
                  className="flex-1 min-h-0"
                  footer={
                    totalCount > 0 ? (
                      <OffsetPagination
                        currentPage={currentPage}
                        totalItems={totalCount}
                        pageSize={PAGE_SIZE}
                        onNextPage={() => setCurrentPage((p) => p + 1)}
                        onPrevPage={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        onPageChange={setCurrentPage}
                        isLoading={isLoading || isFetching}
                        label="contacts"
                      />
                    ) : undefined
                  }
                >
                  <Table
                    columns={columns}
                    data={contacts}
                    keyExtractor={(item: Contact) => item.id}
                    loading={isLoading || isFetching}
                  />
                </TableCard>
              ) : (
                <div className="flex-1 min-h-0 flex items-center justify-center rounded-lg border border-dashed border-foreground/20 px-4 text-center text-sm text-muted-foreground">
                  {contactListPlaceholder({
                    searched: shouldFetch,
                    hasTags: selectedTags.length > 0,
                    isFetching,
                  })}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {showScheduleModal && (
        <ScheduleFlowModal
          isOpen={true}
          onClose={handleScheduleModalClose}
          flowId={flowId}
          flowName={flowName}
          contactCount={addedContactsCount}
          selectedTags={selectedTags}
          flow={{
            id: flowId,
            name: flowName,
            scheduling_window: schedulingWindow,
            schedule_over_unit: scheduleOverUnit,
            allowed_days: allowedDays || [],
            allowed_hours: allowedHours || { start_hour: 9, end_hour: 18 },
            exclude_holidays: excludeHolidays || false,
            transfer_numbers: transferNumbers || {},
            scheduled_for: scheduledFor,
            description: description,
            sub_type: subType,
            flow_type: subType ? 'Engage' : 'Revive',
          }}
        />
      )}
    </>
  );
};
